import { Badge } from "@light-technology/components";
import { Eye } from "@untitledui/icons";
import { Link } from "react-router";
import type { PlaybookContentGuidePreview } from "../../state/api.types";
import { useTypedSelector } from "../../state/store";
import { playbookGuideLink } from "../../utilities/links";
import { useFilteredPlaybookContentOverview } from "./hooks";
import { SkeletonLine } from "./skeletons";

const MAX_GUIDES = 3;

type PlaybookSidebarGuidesProps = {
  section?: string;
};

export const PlaybookSidebarGuides = ({
  section,
}: PlaybookSidebarGuidesProps) => {
  const viewedGuides = useTypedSelector((s) => s.playbook.viewedGuides);
  const { data } = useFilteredPlaybookContentOverview();

  const guideFilter = (
    guide: PlaybookContentGuidePreview | null | undefined,
  ): guide is PlaybookContentGuidePreview => {
    if (!guide) return false;
    if (section && guide.section !== section) return false;
    return true;
  };

  const newestGuides = data?.guides
    ? [...data.guides]
        .filter(guideFilter)
        .sort((a, b) => {
          if (a.published_at === b.published_at) return 0;
          if (!a.published_at) return 1;
          if (!b.published_at) return -1;
          return (
            new Date(b.published_at).getTime() -
            new Date(a.published_at).getTime()
          );
        })
        .slice(0, MAX_GUIDES)
    : [null, null, null];

  // Get the slugs for the 3 most recently viewed guides, then try to find them
  // in the data. While we're waiting on the data we'll show skeletons for the
  // expected count of recent guides.
  const recentGuideSlugs = Object.entries(viewedGuides)
    .sort((a, b) => b[1] - a[1])
    .map(([short_id]) => short_id);
  const recentGuides: (PlaybookContentGuidePreview | null)[] = data?.guides
    ? recentGuideSlugs
        .map((id) => data.guides.find((guide) => guide.short_id === id))
        .filter(guideFilter)
        .slice(0, MAX_GUIDES)
    : [null, null, null].slice(0, recentGuideSlugs.length);

  const groups = [
    {
      title: "What's new",
      guides: newestGuides,
    },
    {
      title: "Recently viewed",
      guides: recentGuides,
    },
  ].filter((g) => g.guides.length > 0);

  if (!groups.length) return null;

  return (
    <div className="flex flex-col gap-8 rounded-2xl bg-tertiary px-xl py-3xl">
      {groups.map(({ title, guides }) => (
        <div key={title} className="flex flex-col gap-md">
          <h3 className="px-md text-sm font-medium">{title}</h3>
          <div className="flex flex-col gap-xs">
            {guides.map((guide, i) => {
              if (!guide)
                return (
                  <div className="px-md py-xs" key={i}>
                    <SkeletonLine className="text-sm" />
                  </div>
                );
              const viewDate = viewedGuides[guide.short_id];
              const publishDate = guide.published_at
                ? new Date(guide.published_at).getTime()
                : null;
              const isUpdated =
                viewDate && publishDate && publishDate > viewDate;
              const isRead = !!viewDate;
              return (
                <Link
                  key={guide.short_id}
                  to={playbookGuideLink(guide)}
                  className="flex items-center gap-md rounded-md px-md py-xs hover:bg-quaternary"
                >
                  <div className="flex-1 truncate text-sm">{guide.title}</div>
                  {isUpdated ? (
                    <Badge size="sm" color="success">
                      Updated
                    </Badge>
                  ) : isRead ? (
                    <Eye className="size-4 text-tertiary" />
                  ) : null}
                </Link>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
};
